import { CoverageDetails, CoverageDocument } from "@graphql/api";
import { restApi } from "@redux/restApi";

interface UploadCoverageDocumentsPayload {
  residentId: string;
  coverageId: string;
  file: File;
}

interface UploadCoverageExtractionDocumentPayload {
  residentId: string;
  file: File;
}

/** Policy data read from a document (the fields the form uses). Null means the value was not found. */
export interface ExtractedExternalCoverage {
  agencyAddress: string | null;
  agencyEmail: string | null;
  agencyName: string | null;
  agencyPhoneNumber: string | null;
  annualPremium: number | null;
  coverageDetails: CoverageDetails;
  insuranceCarrierName: string | null;
  /** YYYY-MM-DD */
  policyEffectiveDate: string | null;
  /** YYYY-MM-DD */
  policyExpirationDate: string | null;
  policyNumber: string | null;
}

export interface CoverageExtractionDocument {
  /** Pass to addExternalCoverage(documentIds) to attach the file to the new coverage. */
  id: string;
  /** Null when the document could not be read. */
  extraction: ExtractedExternalCoverage | null;
}

interface UploadResidentDocumentsPayload {
  residentId: string;
  file: File;
  description?: string;
}

export const uploadDocumentsRestApi = restApi.injectEndpoints({
  endpoints: (build) => ({
    uploadCoverageDocuments: build.mutation<
      CoverageDocument,
      UploadCoverageDocumentsPayload
    >({
      query: ({ file, residentId, coverageId }) => {
        const formData = new FormData();
        formData.append("files", file);

        return {
          url: `residents/${encodeURIComponent(
            residentId
          )}/coverages/${encodeURIComponent(coverageId)}/documents`,
          method: "POST",
          mode: "cors",
          body: formData,
        };
      },
    }),
    /** Stores a policy document for a resident and reads policy data from it. */
    uploadCoverageExtractionDocument: build.mutation<
      CoverageExtractionDocument,
      UploadCoverageExtractionDocumentPayload
    >({
      query: ({ file, residentId }) => {
        const formData = new FormData();
        formData.append("files", file);

        return {
          url: `residents/${encodeURIComponent(
            residentId
          )}/coverage-extraction-documents`,
          method: "POST",
          mode: "cors",
          body: formData,
        };
      },
    }),
    uploadResidentDocuments: build.mutation<
      CoverageDocument,
      UploadResidentDocumentsPayload
    >({
      query: ({ file, residentId, description }) => {
        const formData = new FormData();
        formData.append("files", file);
        formData.append("description", description);

        return {
          url: `/residents/${encodeURIComponent(residentId)}/documents`,
          method: "POST",
          mode: "cors",
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useUploadCoverageDocumentsMutation,
  useUploadCoverageExtractionDocumentMutation,
  useUploadResidentDocumentsMutation,
} = uploadDocumentsRestApi;
